// ----------------------------------------------------------------------

// The words and order on the Careers to-dos card and My to-dos. A due date is
// a day ('YYYY-MM-DD'), compared with today as a day, so nothing shifts with
// time zones.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A local Date → 'YYYY-MM-DD'. */
export const dayOf = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** 'YYYY-MM-DD' → a local Date at midnight, for a date picker. */
export const dateOfDay = (day) => {
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const daysBetween = (from, to) => Math.round((dateOfDay(to) - dateOfDay(from)) / 86400000);

/**
 * When it's due, against today: { text, color } — 'Overdue · Oct 1',
 * 'Due today', 'Due tomorrow', 'Due Oct 20' (with the year when it isn't
 * this year). null when it has no due date or is done.
 */
export function dueLabel(todo, today) {
  if (!todo?.dueDate || todo.completedAt) return null;
  const [y, m, d] = todo.dueDate.split('-').map(Number);
  const short = `${MONTHS[m - 1]} ${d}${String(y) === today.slice(0, 4) ? '' : `, ${y}`}`;
  const days = daysBetween(today, todo.dueDate);
  if (days < 0) return { text: `Overdue · ${short}`, color: 'error' };
  if (days === 0) return { text: 'Due today', color: 'warning' };
  if (days === 1) return { text: 'Due tomorrow', color: 'warning' };
  return { text: `Due ${short}`, color: 'default' };
}

/** Who it's assigned to, from where the reader sits: 'You', 'Student', or their name. */
export function assigneeLabel(todo, userEmail) {
  const { assignee } = todo || {};
  if (!assignee) return '';
  if (assignee.type === 'student') return 'Student';
  if (userEmail && assignee.email?.toLowerCase() === userEmail.toLowerCase()) return 'You';
  return assignee.name || assignee.email || '';
}

/** Open to-dos by due day, undated last (oldest first); for the card and My to-dos. */
export function sortOpen(todos) {
  return [...todos].sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate || b.dueDate) return a.dueDate ? -1 : 1;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });
}

/** Done to-dos, most recently done first. */
export function sortDone(todos) {
  return [...todos].sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));
}

const isMe = (person, userEmail) => !!userEmail && person?.email?.toLowerCase() === userEmail.toLowerCase();

/** As the server decides: its creator or its staff assignee edits it; a super admin anything. */
export const canEditTodo = (todo, userEmail, superAdmin = false) =>
  superAdmin || isMe(todo?.createdBy, userEmail) || (todo?.assignee?.type === 'staff' && isMe(todo.assignee, userEmail));

/** As the server decides: its creator deletes it; a super admin any. */
export const canDeleteTodo = (todo, userEmail, superAdmin = false) => superAdmin || isMe(todo?.createdBy, userEmail);
